<template>
  <div class="content">
    <div class="md-layout">
      <div class="md-layout-item md-size-100">
        <div class="card-wrapper" data-tour="channel_card_webhook">
          <webhooks
            ref="hookListing"
            v-bind:appId="appId"
            v-bind:channelId="id"
            v-on:dirtyHooks="updateDirtyHooks"
            v-on:hooksAutoSave="updateConfig"
            v-on:updateHideSubmitSnackbar="updateHideSubmitSnackbar"
            header-color="primary"
          ></webhooks>
        </div>
      </div>
      <div class="md-layout-item md-size-50 md-small-size-100">
        <div class="card-wrapper" data-tour="channel_card_sfu">
          <edit-sfu-config-form
            v-bind:channel="channel"
            v-bind:ogChannel="ogChannel"
            :simulcastAuthorized="features.simulcast"
            header-color="primary"
          ></edit-sfu-config-form>
        </div>
        <div class="card-wrapper" data-tour="channel_card_audio">
          <edit-audio-codecs-form
            v-bind:channel="channel"
            v-bind:ogChannel="ogChannel"
            header-color="primary"
          ></edit-audio-codecs-form>
        </div>
        <div class="card-wrapper" data-tour="channel_card_video">
          <edit-video-codecs-form
            v-bind:channel="channel"
            v-bind:ogChannel="ogChannel"
            header-color="primary"
          ></edit-video-codecs-form>
        </div>
        <div class="card-wrapper" data-tour="channel_card_rtmp">
          <edit-rtmp-config-form
            v-bind:channel="channel"
            v-bind:ogChannel="ogChannel"
            v-bind:loadingFeature="loadingFeatures"
            :header-color="features.rtmp ? 'primary' : ''"
            :rtmpEnabled="features.rtmp"
          ></edit-rtmp-config-form>
        </div>
      </div>
      <div class="md-layout-item md-size-50 md-small-size-100">
        <div class="card-wrapper" data-tour="channel_card_mcu">
          <edit-mcu-config-form
            v-bind:channel="channel"
            v-bind:ogChannel="ogChannel"
            header-color="primary"
          ></edit-mcu-config-form>
        </div>
        <div class="card-wrapper" data-tour="advanced_recording">
          <edit-recording-config-form
            v-bind:channel="channel"
            v-bind:ogChannel="ogChannel"
            v-bind:recordingAuthorized="features.recording"
            v-bind:loadingFeature="loadingFeatures"
            :header-color="features.recording ? 'primary' : ''"
          ></edit-recording-config-form>
        </div>
      </div>
    </div>
    <submit-snackbar
      v-bind:validationError="validationError"
      v-bind:dirty="dirty"
      v-bind:errorsOccurred="submitErrorsOccurred"
      v-on:submit="updateConfig"
      data-tour="channel_submit"
    ></submit-snackbar>
  </div>
</template>

<script>
import { get } from "vuex-pathify";
import _ from "lodash";
import api from "@/api";
import ChannelModel from "@/models/channel";
import {
  EditMcuConfigForm,
  EditSfuConfigForm,
  EditAudioCodecsForm,
  EditVideoCodecsForm,
  EditRtmpConfigForm,
  EditRecordingConfigForm,
} from "@/pages";
import { Webhooks } from "@/fmComponents";
import { SubmitSnackbar } from "@/fmComponents";
import validationRules from "../../validationRules";

export default {
  components: {
    EditMcuConfigForm,
    EditSfuConfigForm,
    Webhooks,
    EditAudioCodecsForm,
    EditVideoCodecsForm,
    EditRtmpConfigForm,
    EditRecordingConfigForm,
    SubmitSnackbar
  },
  data: () => ({
    dirtyHooks: false, // a data property used to force the computed `dirty` property to be re-calculated.
    hideSubmitSnackbar: false,
    features: {
      recording: false,
      rtmp: false,
      simulcast: false
    },
    loadingFeatures: true,
  }),
  props: {
    id: null,
    appId: null,
  },
  computed: {
    validationError: function () {
      return this.$v.channel.$anyError;
    },
    channel: get("channels/loadedEntity"),
    ogChannel: get("channels/loadedOgEntity"),
    dirty: function () {
      this.$v.channel.$touch();
      var dirtyChannel = !_.isEqual(this.channel, this.ogChannel);
      return !this.hideSubmitSnackbar && (dirtyChannel || this.dirtyHooks);
    },
    submitErrorsOccurred: function () {
      return get("channels/errorsOccurred") && get("webhooks/errorsOccurred");
    },
  },
  created() {
    this.$store.set("webhooks/endPoint", api.props.endPoints.channelWebhook);
    this.loadEntity();
    this.$store
      .dispatch("subscription/loadSubscription", localStorage.subscriptionId)
      .then((result) => {
        Object.entries(this.features).forEach(([feature,_]) => {
          this.features[feature] = result.enabledFeatures.includes(feature);
        });
        this.loadingFeatures = false;
      });
  },
  methods: {
    loadEntity: function () {
      var defaultEntity = new ChannelModel();
      defaultEntity.id = this.id;
      this.$store.dispatch("channels/loadEntity", defaultEntity) || {};
    },
    updateDirtyHooks: function (isDirty) {
      this.dirtyHooks = isDirty;
    },
    updateConfig: function () {
      if (this.validationError) {
        this.$store.commit("pushError", "Your change has one or more errors.");
      } else {
        var me = this;

        if (!_.isEqual(this.channel, this.ogChannel)) {
          // The REST API will not let us send `priority` for the default channel.
          // So, I am testing for that here and unsetting it in this case.
          if (this.channel.default) {
            delete this.channel.priority;
          }

          this.clean(this.channel);

          this.$store
            .dispatch("channels/updateEntity", this.channel)
            .then(() => {
              me.loadEntity();
              this.$store.commit(
                "pushSuccess",
                "Your changes have been saved. They will take effect the next time a channel is activated using this channel configuration."
              );
            })
            .catch(() => {
              me.loadEntity();
            });
        }

        this.$refs.hookListing.updateConfig();
      }
    },
    // This functionality was added during UI change to show app hooks & default channel hooks at the same time.
    // This functionality was needed because the submit snackbar was showing up over the webhook modal, blocking the "apply" button on short screens
    updateHideSubmitSnackbar(newVal) {
      this.hideSubmitSnackbar = newVal == "hidden" ? true : false;
    },
    clean: function () {
      /* Work around Vue's bug where deleting a value froom a number field sets it to an empty string. */
      if (this.channel.codec.opus.bitrate === "") {
        this.channel.codec.opus.bitrate = null;
      }

      if (this.channel.codec.vp8.bitrate === "") {
        this.channel.codec.vp8.bitrate = null;
      }
      if (this.channel.codec.vp9.bitrate === "") {
        this.channel.codec.vp9.bitrate = null;
      }
      if (this.channel.codec.h264.bitrate === "") {
        this.channel.codec.h264.bitrate = null;
      }

      if (this.channel.sfu.audioInput.maxBitrate === "") {
        this.channel.sfu.audioInput.maxBitrate = null;
      }
      if (this.channel.sfu.videoInput.maxBitrate === "") {
        this.channel.sfu.videoInput.maxBitrate = null;
      }

      if (this.channel.mcu.audioInput.maxBitrate === "") {
        this.channel.mcu.audioInput.maxBitrate = null;
      }
      if (this.channel.mcu.videoInput.maxBitrate === "") {
        this.channel.mcu.videoInput.maxBitrate = null;
      }
      if (this.channel.mcu.videoOutput.width === "") {
        this.channel.mcu.videoOutput.width = null;
      }
      if (this.channel.mcu.videoOutput.height === "") {
        this.channel.mcu.videoOutput.height = null;
      }
      if (this.channel.mcu.videoOutput.frameRate === "") {
        this.channel.mcu.videoOutput.frameRate = null;
      }

      if (this.channel.mcu.videoOutput.layoutMargin === "") {
        this.channel.mcu.videoOutput.layoutMargin = null;
      }

      this.channel.videoEncodings.forEach((videoEncoding) => {
        if (videoEncoding.bitrate === "") {
          videoEncoding.bitrate = null;
        }
        if (videoEncoding.frameRate === "") {
          videoEncoding.frameRate = null;
        }
        if (videoEncoding.scale === "") {
          videoEncoding.scale = null;
        }
      });
    },
  },
  validations() {
    return validationRules;
  },
};
</script>
<style>
.card-wrapper {
  display: grid;
}
div.md-toolbar-section-start {
  min-width: 0;
}
h3.md-title {
  display: block !important;
  white-space: nowrap !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
}
div.md-toolbar-section-end {
  flex: unset;
}
</style>
